<template>
    <layout :racingstars="$page.props.racingstars" :jetstream="$page.props.jetstream" :user="$page.props.user" :title="'User Details for '+currentUser.name">
        <template #header>
            {{ 'User Details for '+currentUser.name }}
        </template>
        <div class="sm:p-6">
            <div class="py-4">
                <div class="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
                    <div class="flex items-center space-x-5">
                        <div class="shrink-0">
                            <div class="relative">
                                <span class="inline-block relative">
                                    <img class="h-16 w-16 rounded-full" :src="currentUser.profile_photo_url" :alt="currentUser.name" />
                                    <span class="absolute inset-0 shadow-inner rounded-full" aria-hidden="true" />
                                    <span v-if="currentUser.active" class="absolute top-0 right-0 block h-4 w-4 rounded-full ring-2 ring-white bg-green-400" />
                                    <span v-else class="absolute top-0 right-0 block h-3.5 w-3.5 rounded-full ring-2 ring-white bg-red-400" />
                                </span>
                            </div>
                        </div>
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{{ currentUser.name }}</h1>
                            <p class="text-sm font-medium text-gray-500 dark:text-white dark:text-opacity-75">{{ currentUser.email }}</p>
                        </div>
                    </div>
                    <div class="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
                        <button type="button" class="inline-flex items-center justify-center px-4 py-2 shadow-sm text-sm font-medium rounded-md bg-red-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500" @click="showDeleteUserConfirmationModal = !showDeleteUserConfirmationModal">
                            Delete
                        </button>
                        <button type="button" class="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500" @click="showAdjustBalanceModal = !showAdjustBalanceModal">
                            Adjust Balance
                        </button>
                        <button v-if="!currentUser.verified_at" type="button" class="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500" @click="showVerifyUserConfirmationModal = !showVerifyUserConfirmationModal">
                            Manually Verify
                        </button>
                    </div>
                </div>
                <div class="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                    <div class="space-y-6 lg:col-start-1 lg:col-span-2">
                        <section aria-labelledby="applicant-information-title">
                            <div class="bg-white shadow sm:rounded-lg">
                                <div class="px-4 py-5 sm:px-6">
                                    <h2 id="applicant-information-title" class="text-lg leading-6 font-medium text-gray-900">
                                        User Details
                                    </h2>
                                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                                        Personal and verification details.
                                    </p>
                                </div>
                                <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                                    <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Verification Status
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ currentUser.decision ? currentUser.decision : 'Unavailable' }}
                                            </dd>
                                        </div>
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Verification Score
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ currentUser.score ? currentUser.score : 'Unavailable' }}
                                            </dd>
                                        </div>
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Authentication ID
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ currentUser.authentication_id ? currentUser.authentication_id : 'Unavailable' }}
                                            </dd>
                                        </div>
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Mobile Number
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ currentUser.mobile }}
                                            </dd>
                                        </div>
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Date of Birth
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ moment(currentUser.date_of_birth).format('MMMM Do YYYY') }}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div class="space-y-6 lg:col-start-1 lg:col-span-2">
                        <section aria-labelledby="applicant-information-title">
                            <div class="bg-white shadow sm:rounded-lg">
                                <div class="px-4 py-5 sm:px-6">
                                    <h2 id="applicant-information-title" class="text-lg leading-6 font-medium text-gray-900">
                                        Account Details
                                    </h2>
                                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                                        Balance and token information.
                                    </p>
                                </div>
                                <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                                    <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Current Balance
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ formatter.format(currentUser.balance / 100) }}
                                            </dd>
                                        </div>
                                        <div class="sm:col-span-1">
                                            <dt class="text-sm font-medium text-gray-500">
                                                Current Tokens
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ currentUser.play_tokens }}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>

        <tw-modal :show="showVerifyUserConfirmationModal" @close="showVerifyUserConfirmationModal = false" title="Manually Verify User?">
            <jet-form-section @submitted="verifyUser">
                <template #form>
                    <div v-if="showVerifyUserConfirmationModal" class="mt-1 relative rounded-md shadow-sm mb-4">
                        Are you sure you want to manually verify the user {{ currentUser.first_name }} {{ currentUser.last_name }}?
                    </div>
                </template>
                    
                <template #actions>
                    <jet-button type="button" class="text-right mr-2" :ignore-dark-mode="true" @click="showVerifyUserConfirmationModal = false">
                        Cancel
                    </jet-button>
                    <jet-button :ignore-dark-mode="true" class="bg-red-700 text-gray-100 dark:bg-red-700 dark:text-gray-100">
                        Confirm
                    </jet-button>
                </template>
            </jet-form-section>
        </tw-modal>

        <tw-modal :show="showAdjustBalanceModal" @close="showAdjustBalanceModal = false" title="Manually Verify User?">
            <jet-form-section @submitted="adjustBalance">
                <template #form>
                    <div class="mt-1 relative rounded-md shadow-sm mb-4">
                        <jet-label for="balance" value="Balance" />
                        <jet-input type="number" :ignore-dark-mode="true" name="balance" v-model="updateForm.balance" placeholder="Enter a New Balance Amount" class="mt-1 block w-full p-2" />
                        <div v-show="updateForm.errors.balance" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <ExclamationCircleIcon class="h-5 w-5 text-red-500" aria-hidden="true" />
                        </div>
                        <jet-input-error :message="updateForm.errors.balance" class="mt-2" />
                    </div>
                </template>
                    
                <template #actions>
                    <jet-secondary-button type="button" class="text-right mr-2" :ignore-dark-mode="true" @click="showAdjustBalanceModal = false">
                        Cancel
                    </jet-secondary-button>
                    <jet-button :ignore-dark-mode="true">
                        Confirm
                    </jet-button>
                </template>
            </jet-form-section>
        </tw-modal>

        <tw-modal :show="showDeleteUserConfirmationModal" @close="showDeleteUserConfirmationModal = false" title="Delete User?">
            <jet-form-section @submitted="deleteUser">
                <template #form>
                    <div v-if="showDeleteUserConfirmationModal" class="mt-1 relative rounded-md shadow-sm mb-4">
                        Are you sure you want to delete the user {{ currentUser.first_name }} {{ currentUser.last_name }}?
                    </div>
                </template>
                    
                <template #actions>
                    <jet-button type="button" class="text-right mr-2" :ignore-dark-mode="true" @click="showDeleteUserConfirmationModal = false">
                        Cancel
                    </jet-button>
                    <jet-button :ignore-dark-mode="true" class="bg-red-700 text-gray-100 dark:bg-red-700 dark:text-gray-100">
                        Confirm
                    </jet-button>
                </template>
            </jet-form-section>
        </tw-modal>
    </layout>
</template>
<script>
import { ref, getCurrentInstance } from 'vue'
import Layout from '@/Layouts/Layout'
import moment from 'moment'
import JetButton from '@/Jetstream/Button.vue'
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue'
import JetFormSection from '@/Jetstream/FormSection.vue'
import JetLabel from '@/Jetstream/Label.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetInputError from '@/Jetstream/InputError.vue'
import { Inertia } from '@inertiajs/inertia'
import { ExclamationCircleIcon } from '@heroicons/vue/solid'

export default {
    props: {
        currentUser: {
            type: Object,
        }
    },
    components: {
        Layout,
	    JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetSecondaryButton,
	    JetFormSection,
        ExclamationCircleIcon,
    },
    setup(props) {

		const showVerifyUserConfirmationModal = ref(false)
        const showAdjustBalanceModal = ref(false)
		const showDeleteUserConfirmationModal = ref(false)

		const updateForm = Inertia.form({
            balance: (props.currentUser.balance / 100).toString(),
            action: null,
            verified: false,
        })

        /**
         * Formatter function required to format currencies/prices correctly.
         * 
         * @type {Intl}
         */
        const formatter = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
        })

		const verifyUser = () => {
			updateForm
        		.transform((data) => ({
                    ...data,
        			action: 'updateVerifiedAt',
        			verified: true,
        		}))
        		.patch(route('admin.users.update', { id: props.currentUser.id }), {
	        		onSuccess: () => {
	        			updateForm.reset()
	        			showVerifyUserConfirmationModal.value = false
	        		}
	        	})
		}

        const adjustBalance = () => {
            updateForm
                .transform((data) => ({
                    ...data,
                    action: 'adjustBalance',
                }))
                .patch(route('admin.users.update', { id: props.currentUser.id }), {
                    onSuccess: () => {
                        updateForm.balance = (props.currentUser.balance / 100).toString()
                        showAdjustBalanceModal.value = false
                    },
                    onError: (error) => {
                        console.log(error)
                    }
                })
        }

        const deleteUser = () => {
        	Inertia.delete(route('admin.users.destroy', { id: props.currentUser.id }), {
        		onSuccess: () => {
        			showDeleteUserConfirmationModal.value = false
        		}
        	})
        }

    	return {
    		moment,
    		showVerifyUserConfirmationModal,
    		showAdjustBalanceModal,
            showDeleteUserConfirmationModal,
    		verifyUser,
            adjustBalance,
            updateForm,
    		deleteUser,
            formatter,
    	}
    }
}

</script>
